<template>
  <div>
    <v-container
      v-if="isLoading"
      class="d-flex justify-center align-center"
      style="height: 100vh;"
    >
      <v-progress-circular
        :size="100"
        color="#2661FF"
        indeterminate
      ></v-progress-circular>
    </v-container>
    <v-container v-else class="px-5 my-5">
      <h4 class="title">{{ $t("transactions.uniques") }}</h4>
      <v-row no-gutters>
        <v-col cols="12">
          <DonationTable
            :loading="transactionTable.isLoading"
            :table="transactionTable"
            :startDate="date.startDate"
            :endDate="date.endDate"
            :organizations="organizations"
            :states="states"
            :gateways="filteredGateways"
            :dateRange="date.dateRange"
            @get-transactions="getTransactions"
            @update-dates="updateDates"
            @clear-search="clearSearch"
            @table-pagination="transactionTable.pagination = $event"
            @open-details="openTransactionDrawer"
            @active-export-dialog="toggleDialogExport(true)"
          />
        </v-col>
      </v-row>
      <DialogExport
        :module-options="['DRBTransactions']"
        :organization_id="transactionTable.organization_id"
        :startDate="date.startDate"
        :endDate="date.endDate"
        :filters="filters"
      />
      <DialogMessage />

      <v-navigation-drawer
        v-if="isTransactionDetailDrawerOpen"
        :value="isTransactionDetailDrawerOpen"
        @input="inputTransactionDetailDrawer"
        width="50%"
        max-width="100vw"
        right
        fixed
        temporary
        style="transition: 0.5s cubic-bezier(0.4, 0, 0.2, 1);"
      >
        <TransactionDetail />
      </v-navigation-drawer>
    </v-container>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import DateFilters from "@/components/Common/DateFilters";
import DateBar from "@/components/Common/DateBar";
import donationMixin from "@/mixins/donations";
import DialogExport from "@/components/Downloads/DialogExport";
import DialogMessage from "@/components/Downloads/DialogMessage";
import TransactionDetail from "@/components/Transactions/Details";
import DonationTable from "@/components/Common/DonationTable";

export default {
  name: "UniqueTransactions",
  mixins: [donationMixin],
  components: {
    DateFilters,
    DateBar,
    DialogExport,
    DialogMessage,
    TransactionDetail,
    DonationTable
  },
  data() {
    return {
      isLoading: false,
      date: {
        startDate: this.$moment()
          .startOf("month")
          .format("YYYY-MM-DD"),
        endDate: this.$moment()
          .endOf("day")
          .format("YYYY-MM-DD"),
        dateRange: 6
      },
      transactionTable: {
        isLoading: false,
        items: [],
        total: 0,
        filters: {
          search: null,
          organization: null,
          gateway: null,
          state: null
        },
        pagination: {
          sortBy: ["created_at"],
          sortDesc: [true],
          itemsPerPage: 25
        },
        headers: [
          {
            text: "ID",
            value: "id",
            align: "left",
            sortable: true,
            class: "header_color"
          },
          {
            text: this.$t("transactions.dashboard.timestamp"),
            value: "created_at",
            align: "left",
            sortable: true,
            class: "header_color"
          },
          {
            text: this.$t("transactions.dashboard.donor"),
            value: "lead.first_name",
            align: "left",
            sortable: false,
            class: "header_color",
            width: "200px"
          },
          {
            text: this.$t("transactions.dashboard.amount"),
            value: "conversion_amount",
            align: "left",
            sortable: true,
            class: "header_color"
          },
          {
            text: this.$t("transactions.dashboard.status"),
            value: "state",
            align: "left",
            sortable: true,
            class: "header_color"
          },
          {
            text: this.$t("transactions.dashboard.organization"),
            value: "organization.name",
            align: "left",
            sortable: true,
            class: "header_color"
          },
          {
            text: this.$t("transactions.dashboard.payment_method"),
            value: "payment_method",
            align: "left",
            sortable: false,
            class: "header_color"
          },
          {
            text: this.$t("transactions.dashboard.gateway"),
            value: "gateway.name",
            align: "left",
            sortable: false,
            class: "header_color"
          }
        ]
      },
      states: [
        {
          name: this.$t("subscriptions.status.canceled"),
          value: this.getStateValueByName("cancel")
        },
        {
          name: this.$t("transactions.status.succeeded"),
          value: this.getStateValueByName("success")
        },
        {
          name: this.$t("transactions.status.pending"),
          value: this.getStateValueByName("pending")
        },
        {
          name: this.$t("transactions.status.failed"),
          value: this.getStateValueByName("failed")
        }
      ]
    };
  },
  created() {
    this.date.startDate = this.$moment(this.creationDateUser).format(
      "YYYY-MM-DD"
    );
  },
  async mounted() {
    this.isLoading = true;
    await this.getOrganizations();
    await this.getGateways();
    this.isLoading = false;
  },
  watch: {
    "transactionTable.pagination": {
      async handler() {
        await this.getTransactions();
      },
      deep: true
    }
  },
  computed: {
    ...mapState({
      transactions: ({ transactions: { transactions } }) => transactions,
      transactionsTotal: ({ transactions: { transactionsTotal } }) =>
        transactionsTotal,
      organizations: ({ organizations: { organizations } }) => organizations,
      gateways: ({ gateways: { gateways } }) => gateways,
      creationDateUser: ({ auth: { user } }) => user.created_at,
      isTransactionDetailDrawerOpen: ({
        transactions: { isTransactionDetailDrawerOpen }
      }) => isTransactionDetailDrawerOpen
    }),
    filters() {
      const { transactionTable } = this;
      return {
        gateway: transactionTable.filters.gateway,
        state: transactionTable.filters.state,
        search: transactionTable.filters.search,
        organization: transactionTable.filters.organization,
        type: "uniques"
      };
    },
    filteredGateways() {
      const PAYMENTS_WAY = "Bakery";
      const RECURRENT_PAYMENTS = "Recurrent Payments";

      const allowedGateways = [PAYMENTS_WAY, RECURRENT_PAYMENTS];

      return this.gateways
        .filter(gateway => {
          const { name } = gateway;
          return allowedGateways.includes(name);
        })
        .map(gateway => {
          // Display Payments way instead of Bakery
          if (gateway.name === PAYMENTS_WAY) {
            return { ...gateway, display_name: "Payments Way" };
          } else {
            return { ...gateway, display_name: gateway.name };
          }
        });
    }
  },
  methods: {
    ...mapActions({
      getOrganizations: "organizations/getOrganizations",
      getGateways: "gateways/getGateways",
      fetchTransactions: "transactions/getTransactions",
      openTransactionDetailsDrawer: "transactions/openTransactionDrawer"
    }),
    ...mapMutations({
      toggleDialogExport: "downloads/toggleDialogExport",
      setTransactionDetailsDrawer: "transactions/setTransactionDetailsDrawer"
    }),
    updateDates(property, date) {
      this.date[property] = date;
      this.getTransactions();
    },
    async getTransactions() {
      const { transactionTable, date } = this;
      const { page, itemsPerPage: rowsPerPage } = transactionTable.pagination;
      let { sortBy, sortDesc: descending } = transactionTable.pagination;
      const { search, organization, gateway, state } = transactionTable.filters;

      const UNIQUE_TRANSACTION = "uniques";

      if (Array.isArray(sortBy)) {
        [sortBy] = sortBy;
      }
      if (Array.isArray(descending)) {
        [descending] = descending;
      }

      const payload = {
        startDate: date.startDate,
        endDate: date.endDate,
        page,
        rowsPerPage,
        sortBy,
        descending,
        search,
        organization,
        gateway,
        state,
        type: UNIQUE_TRANSACTION
      };
      this.transactionTable.isLoading = true;

      await this.fetchTransactions(payload);

      this.transactionTable.items = this.transactions;
      this.transactionTable.total = Number(
        parseInt(this.transactionsTotal, 10)
      );

      this.transactionTable.isLoading = false;
    },
    getStatusIcon(state) {
      const SUCCESS = this.getStateValueByName("success");
      const ACTIVE = this.getStateValueByName("active");

      switch (state) {
        case SUCCESS:
        case ACTIVE:
          return "done";
        default:
          return "warning";
      }
    },
    formatLeadName(lead) {
      return lead ? `${lead.first_name} ${lead.last_name}` : "-";
    },
    formatOrganizationName(organization) {
      return organization ? organization.name : "-";
    },
    formatGatewayName(gateway) {
      const PAYMENTS_WAY = "Bakery";
      const RECURRENT_PAYMENTS = "Recurrent Payments";

      if (gateway.gateway) {
        const { name } = gateway.gateway;

        switch (name) {
          case PAYMENTS_WAY:
            return "Payments Way";
          case RECURRENT_PAYMENTS:
            return RECURRENT_PAYMENTS;
          default:
            return "-";
        }
      }
    },
    formatCurrencyCode(amount, symbol, code) {
      if (amount && symbol && code) {
        return `${symbol} ${amount} ${code}`;
      } else if (amount && symbol) {
        return `${symbol} ${amount}`;
      } else if (amount) {
        return amount;
      } else {
        return "-";
      }
    },
    async clearSearch() {
      this.transactionTable.filters.search = null;
      await this.getTransactions();
    },
    async openTransactionDrawer(transaction) {
      const { id: transaction_id } = transaction;

      await this.openTransactionDetailsDrawer({ transaction_id });
    },
    inputTransactionDetailDrawer(arg) {
      if (!arg) {
        this.setTransactionDetailsDrawer(false);
      }
    }
  }
};
</script>

<style>
.card {
  border: 1px solid #d0d5dd !important;
  box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.15) !important;
}

.header_color {
  color: #2661ff !important;
}
</style>
